import { ReactNode, useState } from 'react';
import { Grid, Box, Button, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AssessmentResult as AssessmentResultType } from '../../types/assessment';
import ScoreSummaryCard from './ScoreSummaryCard';
import RecommendationCard from './RecommendationCard';
import ScoringIndexesCard from './ScoringIndexesCard';
import FindingsCard from './FindingsCard';
import DetailedScorecardCard from './DetailedScorecardCard';
import FullAnalysisCard from './FullAnalysisCard';
import AssessmentIcon from '@mui/icons-material/Assessment';

interface Props {
  result: AssessmentResultType;
  extraActions?: ReactNode;
}

export default function AssessmentResult({ result, extraActions }: Props) {
  const navigate = useNavigate();
  const [visibleCards, setVisibleCards] = useState<Record<string, boolean>>({});

  const toggleCard = (id: string) => {
    setVisibleCards(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleGoToDashboard = () => {
    if (result.source_id) {
      navigate(`/dashboard/${result.source_id}`);
    }
  };

  return (
    <Box>
      <div className="version-tag">Trust Index &middot; System Prompt v3.0 &middot; April 2026</div>

      {/* Always-visible cards */}
      {/* Score summary — always full width */}
      <ScoreSummaryCard result={result} />

      <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
        <Grid size={12}>
          <RecommendationCard result={result} />
        </Grid>
      </Grid>

      {/* Reveal buttons */}
      <Box
        className="reveal-btn-row"
        sx={{
          mt: 1.5,
          mb: 1.5,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <button className={`reveal-btn ${visibleCards['indexes'] ? 'shown' : ''}`} onClick={() => toggleCard('indexes')}>
            <span className="btn-icon" /> Scoring indexes
          </button>
          <button className={`reveal-btn ${visibleCards['findings'] ? 'shown' : ''}`} onClick={() => toggleCard('findings')}>
            <span className="btn-icon" /> Findings
          </button>
          <button className={`reveal-btn ${visibleCards['scorecard'] ? 'shown' : ''}`} onClick={() => toggleCard('scorecard')}>
            <span className="btn-icon" /> Detailed scorecard
          </button>
          <button className={`reveal-btn ${visibleCards['analysis'] ? 'shown' : ''}`} onClick={() => toggleCard('analysis')}>
            <span className="btn-icon" /> Full analysis
          </button>
        </Box>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          {extraActions}
          {result.source_id && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AssessmentIcon />}
              onClick={handleGoToDashboard}
              sx={{ borderRadius: '20px', textTransform: 'none' }}
            >
              View Source Dashboard
            </Button>
          )}
        </Stack>
      </Box>

      {/* Expandable cards in responsive grid */}
      <Grid container spacing={1.5}>
        {visibleCards['indexes'] && (
          <Grid size={{ xs: 12, lg: visibleCards['findings'] ? 6 : 12 }}>
            <ScoringIndexesCard result={result} />
          </Grid>
        )}
        {visibleCards['findings'] && (
          <Grid size={{ xs: 12, lg: visibleCards['indexes'] ? 6 : 12 }}>
            <FindingsCard result={result} />
          </Grid>
        )}
        {visibleCards['scorecard'] && (
          <Grid size={{ xs: 12, lg: visibleCards['analysis'] ? 6 : 12 }}>
            <DetailedScorecardCard result={result} />
          </Grid>
        )}
        {visibleCards['analysis'] && (
          <Grid size={{ xs: 12, lg: visibleCards['scorecard'] ? 6 : 12 }}>
            <FullAnalysisCard result={result} />
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
